import React, { useState } from "react";
import axios from "axios";
import { Sparkles, Loader2, RotateCcw, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScoreRing = ({ score }) => {
  const pct = Math.max(0, Math.min(100, score));
  const dash = 251.2; // 2*pi*40
  const offset = dash - (dash * pct) / 100;
  let label = "Generic";
  let tone = "text-[#5C5C5C]";
  if (pct >= 75) { label = "Genuinely Novel"; tone = "text-[#002FA7]"; }
  else if (pct >= 55) { label = "Has An Angle"; tone = "text-[#0A0A0A]"; }
  else if (pct >= 35) { label = "Crowded Space"; tone = "text-[#5C5C5C]"; }
  return (
    <div className="flex items-center gap-5">
      <div className="relative w-24 h-24 shrink-0">
        <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
          <circle cx="50" cy="50" r="40" stroke="rgba(10,10,10,0.10)" strokeWidth="6" fill="none" />
          <circle
            cx="50" cy="50" r="40" stroke="#002FA7" strokeWidth="6" fill="none"
            strokeDasharray={dash} strokeDashoffset={offset} strokeLinecap="square"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-serif text-3xl font-light tracking-tight">{pct}</span>
        </div>
      </div>
      <div>
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C]">Originality</div>
        <div className={`font-serif text-xl ${tone}`}>{label}</div>
        <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C] mt-1">{pct} / 100</div>
      </div>
    </div>
  );
};

const ColList = ({ eyebrow, items, marker, tone = "default" }) => (
  <div>
    <div className={`font-mono text-[10px] tracking-[0.22em] uppercase mb-3 ${tone === "klein" ? "text-[#002FA7]" : "text-[#5C5C5C]"}`}>
      {eyebrow}
    </div>
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed text-[#0A0A0A]/85">
          <span className={`font-mono text-xs shrink-0 ${tone === "klein" ? "text-[#002FA7]" : "text-[#0A0A0A]/50"}`}>
            {marker}
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const EvaluatorTool = ({ onUpgradeClick }) => {
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!idea.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/evaluator/quick`, { idea: idea.trim() });
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Evaluation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="evaluator"
      data-testid="evaluator-section"
      className="border-y border-[#0A0A0A]/12 bg-[#EFEFE8]/40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        <div className="mb-10">
          <h2 className="font-serif font-light text-3xl sm:text-5xl tracking-tighter leading-[1.0] text-[#0A0A0A] max-w-3xl">
            Stress-test your idea in <em className="not-italic text-[#002FA7]">5 seconds.</em>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-[#0A0A0A]/70 max-w-2xl leading-relaxed">
            Get a brutally-honest snap verdict, an originality score, your top three strengths and risks, and three concrete next moves. No signup. Always free.
          </p>
        </div>

        <form onSubmit={submit} className="grid grid-cols-12 gap-0 -mb-px" data-testid="evaluator-form">
          <textarea
            data-testid="evaluator-input"
            value={idea}
            onChange={(e) => setIdea(e.target.value.slice(0, 1000))}
            required
            rows={3}
            placeholder="Drop your one-liner. E.g., 'A WhatsApp-first AI tutor for Class 9–12 ICSE physics that solves problems from a photo of the textbook.'"
            className="col-span-12 md:col-span-9 bg-[#F9F8F6] border border-[#0A0A0A]/15 p-5 font-sans text-base focus:border-[#002FA7] focus:outline-none resize-none -ml-px -mt-px"
            maxLength={1000}
          />
          <button
            type="submit"
            disabled={loading || !idea.trim()}
            data-testid="evaluator-submit"
            className="col-span-12 md:col-span-3 bg-[#0A0A0A] text-[#F9F8F6] font-mono text-xs tracking-[0.22em] uppercase px-6 py-5 hover:bg-[#002FA7] disabled:opacity-50 flex items-center justify-center gap-3 -ml-px -mt-px"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</> : <><Sparkles className="w-4 h-4" /> Evaluate</>}
          </button>
        </form>
        <div className="flex items-baseline justify-between mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C]">
          <span>{idea.length} / 1000</span>
          <span>~3 sec response · 100% free</span>
        </div>

        {error && (
          <div data-testid="evaluator-error" className="mt-6 border border-[#0A0A0A] bg-[#0A0A0A] text-[#F9F8F6] p-4 font-mono text-xs">
            ERR · {error}
          </div>
        )}

        {(loading || result) && (
          <div className="mt-12 border border-[#0A0A0A]/15 bg-[#F9F8F6]" data-testid="evaluator-result">
            {loading ? (
              <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="shimmer h-24" />
                <div className="shimmer h-24 md:col-span-2" />
                <div className="shimmer h-32 md:col-span-3" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-12 gap-0 border-b border-[#0A0A0A]/12">
                  <div className="col-span-12 md:col-span-4 p-7 border-r border-[#0A0A0A]/12 bg-[#F9F8F6]">
                    <ScoreRing score={result.originality_score} />
                  </div>
                  <div className="col-span-12 md:col-span-8 p-7 flex flex-col justify-center">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#002FA7] mb-2">
                      Verdict
                    </div>
                    <p className="font-serif text-2xl sm:text-3xl leading-tight tracking-tight text-[#0A0A0A]">
                      "{result.verdict}"
                    </p>
                    <div className="mt-4 pt-4 border-t border-[#0A0A0A]/12">
                      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-1">
                        One-Liner Pitch
                      </div>
                      <p className="text-sm text-[#0A0A0A]/85 leading-relaxed">{result.one_liner_pitch}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                  <div className="p-7 border-r border-[#0A0A0A]/12">
                    <ColList eyebrow="Strengths · 03" items={result.strengths} marker="+" tone="klein" />
                  </div>
                  <div className="p-7 border-r border-[#0A0A0A]/12 bg-[#0A0A0A] text-[#F9F8F6]">
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase mb-3 text-[#F9F8F6]/60">
                      Risks · 03
                    </div>
                    <ul className="space-y-2.5">
                      {result.risks.map((it, i) => (
                        <li key={i} className="flex gap-3 text-sm leading-relaxed text-[#F9F8F6]/90">
                          <span className="font-mono text-xs text-[#F9F8F6]/50 shrink-0">×</span>
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-7">
                    <ColList eyebrow="Next Moves · 03" items={result.next_steps} marker="→" tone="klein" />
                  </div>
                </div>

                <div className="border-t border-[#0A0A0A]/12 bg-[#EFEFE8] px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C]">
                    Evaluated in {result.latency_ms} ms
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setResult(null); setIdea(""); setError(null); }}
                      data-testid="evaluator-reset"
                      className="border border-[#0A0A0A]/25 font-mono text-[10px] tracking-[0.22em] uppercase px-4 py-2 hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-2"
                    >
                      <RotateCcw className="w-3 h-3" /> Try Another
                    </button>
                    <button
                      onClick={() => onUpgradeClick?.(result.idea)}
                      data-testid="evaluator-upgrade"
                      className="bg-[#002FA7] text-white font-mono text-[10px] tracking-[0.22em] uppercase px-4 py-2 hover:bg-[#0A0A0A] flex items-center gap-2"
                    >
                      Generate Full Memo <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default EvaluatorTool;
